import { Login } from '../types'

const LOGIN_STORAGE_VERSION_LITE = 'lite-v1'
const LOGIN_STORAGE_VERSION_FULL = 'full-v1'

export interface VersionedLogin extends Login {
  _version?: string
}

/**
 * Migrates legacy full login payload to lite format
 * Extracts only the fields needed for routing/initial render
 */
function extractLiteFields(fullPayload: Login): VersionedLogin {
  return {
    status: fullPayload.status,
    type: fullPayload.type,
    eaterDTO: fullPayload.eaterDTO
      ? {
          eaterId: fullPayload.eaterDTO.eaterId,
          step: fullPayload.eaterDTO.step,
          imageUrl: fullPayload.eaterDTO.imageUrl,
          subscribed: fullPayload.eaterDTO.subscribed,
        }
      : undefined,
    restaurantDTO: fullPayload.restaurantDTO
      ? {
          restaurantId: fullPayload.restaurantDTO.restaurantId,
          placeId: fullPayload.restaurantDTO.placeId,
          alias: fullPayload.restaurantDTO.alias,
          name: fullPayload.restaurantDTO.name
        }
      : undefined,
    chefDTO: fullPayload.chefDTO
      ? {
          id: fullPayload.chefDTO.id,
        }
      : undefined,
    _version: LOGIN_STORAGE_VERSION_LITE,
  }
}

/**
 * Migrates stored login payload from legacy full format to lite format
 * Returns lite shape regardless of stored format
 */
export function migrateLoginPayload(stored: string | null): VersionedLogin {
  if (!stored) {
    return { _version: LOGIN_STORAGE_VERSION_LITE }
  }

  try {
    const parsed = JSON.parse(stored) as VersionedLogin

    // Already migrated or new format
    if (parsed._version === LOGIN_STORAGE_VERSION_LITE) {
      return parsed
    }

    // Legacy full payload - extract lite fields
    if (parsed._version === LOGIN_STORAGE_VERSION_FULL || !parsed._version) {
      const lite = extractLiteFields(parsed as Login)
      // Optionally rewrite to localStorage to avoid repeated migration
      // (commented out to avoid side effects in selector)
      // window.localStorage.setItem('login', JSON.stringify(lite))
      return lite
    }

    // Unknown version - return as-is but mark as lite
    return {
      ...parsed,
      _version: LOGIN_STORAGE_VERSION_LITE,
    }
  } catch (error) {
    // Corrupted data - return empty
    return { _version: LOGIN_STORAGE_VERSION_LITE }
  }
}

/**
 * Adds version field to login payload before storing
 */
export function addVersionToLoginPayload(login: Login): VersionedLogin {
  return {
    ...login,
    _version: LOGIN_STORAGE_VERSION_LITE,
  }
}

